import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { PropsWithChildren } from 'react';

import { useRiskSmartForm } from './RiskSmartFormContext';
import style from './style.module.scss';

type Props = {
  value: unknown;
  isDraggable: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  hasInsertionLine?: boolean;
};

function DraggableField({
  value,
  isDraggable,
  children,
  onDragStart,
  onDragEnd,
  hasInsertionLine = false,
}: PropsWithChildren<Props>) {
  const { editMode } = useRiskSmartForm();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: value as string,
    disabled: !isDraggable || !editMode,
  });

  const styleTransform = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isActive = isDraggable && editMode;

  // Only spread DnD attributes/listeners when actively draggable.
  // @dnd-kit always adds role="button", tabIndex=0, and aria-roledescription
  // even when disabled, which breaks form field interactions and E2E selectors.
  const dragAttributes = isActive ? attributes : {};
  const dragListeners = isActive ? listeners : {};

  // When not in edit mode, render children directly without DnD wrappers
  if (!editMode) {
    return <>{children}</>;
  }

  return (
    <div className={'relative'}>
      {hasInsertionLine && (
        <div
          className={
            'absolute top-0 left-0 right-0 -mt-2 flex justify-center z-50'
          }
        >
          <div className={'h-1 w-full bg-teal-500 rounded-full shadow-lg'} />
        </div>
      )}
      <div
        ref={setNodeRef}
        style={styleTransform}
        {...dragAttributes}
        {...dragListeners}
        className={`relative bg-white rounded-md flex items-center break-all ${
          isDragging && isDraggable
            ? 'z-50 border-2 border-teal cursor-grabbing shadow-xl opacity-50'
            : ''
        } ${
          isDraggable
            ? `shadow-sm mb-3 border border-solid border-grey200 p-5 ${
                isDragging ? style.grabbing : style.grabbable
              }`
            : ''
        }`}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        <div className={`flex-1 ${style.children}`}>{children}</div>
      </div>
    </div>
  );
}

export default DraggableField;
